import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ArticleForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetchArticles, showToast } = useApp();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [isJokeCollection, setIsJokeCollection] = useState(false);
    const [jokesList, setJokesList] = useState([{ title: '', content: '' }]);
    const [imageSourceType, setImageSourceType] = useState('url'); // 'url' or 'local'
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    // Add this state with other useState declarations
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    // Add this function to generate slug from title
    const generateSlugFromTitle = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')           // Remove special characters
            .replace(/\s+/g, '-')                    // Replace spaces with hyphens
            .replace(/-+/g, '-')                     // Replace multiple hyphens with single
            .replace(/^-|-$/g, '');                  // Remove hyphens from start/end
    };

    // Add this function to handle title change with auto-slug generation
    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setFormData({ ...formData, title: newTitle });

        // Auto-generate slug only if not manually edited
        if (!slugManuallyEdited) {
            const newSlug = generateSlugFromTitle(newTitle);
            setFormData(prev => ({ ...prev, slug: newSlug }));
        }
    };

    // Add this function to handle manual slug edit
    const handleSlugChange = (e) => {
        const newSlug = e.target.value
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

        setFormData({ ...formData, slug: newSlug });
        setSlugManuallyEdited(true);
    };

    // Add auto-generate button function
    const autoGenerateSlug = () => {
        const newSlug = generateSlugFromTitle(formData.title);
        setFormData({ ...formData, slug: newSlug });
        setSlugManuallyEdited(false);
        showToast('✅ Slug auto-generated from title');
    };

    // Quill toolbar configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['blockquote', 'code-block'],
            ['link', 'image', 'video'],
            ['clean']
        ],
    };

    const quillFormats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'color', 'background', 'list', 'indent', 'align',
        'blockquote', 'code-block', 'link', 'image', 'video'
    ];

    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        section: 'Top Stories',
        author: 'BharatNews Desk',
        source: 'Staff',
        tags: '',
        img: '',
        imgData: '',
        status: 'live',
        breaking: 'no',
        featured: 'no',
        date: new Date().toISOString().split('T')[0],
        seo: {
            metaTitle: '',
            metaDescription: '',
            metaKeywords: '',
            canonicalUrl: '',
            ogTitle: '',
            ogDescription: '',
            ogImage: '',
            twitterTitle: '',
            twitterDescription: '',
            twitterImage: '',
            focusKeyword: ''
        }
    });

    useEffect(() => {
        if (id) {
            fetchArticle();
        }
    }, [id]);

    const fetchArticle = async () => {
        try {
            const res = await api.get(`/articles/${id}`);
            setFormData(res.data);
            if (res.data.imgData) {
                setImageSourceType('local');
                setImagePreview(res.data.imgData);
            } else if (res.data.img) {
                setImageSourceType('url');
                setImagePreview(res.data.img);
            }
            if (res.data.content && res.data.content.includes('joke-collection')) {
                setIsJokeCollection(true);
                parseJokesFromContent(res.data.content);
            }
        } catch (error) {
            console.error('Error fetching article:', error);
            showToast('Error fetching article', 'error');
        }
    };

    const parseJokesFromContent = (htmlContent) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const jokeItems = doc.querySelectorAll('.joke-item');
        if (jokeItems.length > 0) {
            const jokes = [];
            jokeItems.forEach((item, index) => {
                const header = item.querySelector('.joke-header h3');
                const content = item.querySelector('.joke-content');
                jokes.push({
                    title: header ? header.textContent.replace(`Joke ${index + 1}`, '').replace('😂', '').trim() : '',
                    content: content ? content.innerText : ''
                });
            });
            if (jokes.length) setJokesList(jokes);
        }
    };

    const generateJokeContent = () => {
        let html = '<div class="joke-collection">';
        jokesList.forEach((joke, idx) => {
            if (joke.content.trim()) {
                html += `
                    <div class="joke-item">
                        <div class="joke-header">
                            <span class="joke-number">${idx + 1}</span>
                            <h3>${escapeHtml(joke.title || `Joke ${idx + 1}`)}</h3>
                        </div>
                        <div class="joke-content">
                            ${joke.content.replace(/\n/g, '<br/>')}
                        </div>
                        <div class="joke-actions">
                            <button class="joke-like-btn">👍 <span>0</span></button>
                            <button class="joke-share-btn">📤 Share</button>
                        </div>
                    </div>
                `;
            }
        });
        html += '</div>';
        return html;
    };

    const escapeHtml = (text) => {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleContentChange = (value) => {
        setFormData({ ...formData, content: value });

        if (!formData.excerpt && value) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = value;
            const plainText = tempDiv.textContent || tempDiv.innerText || '';
            const excerpt = plainText.substring(0, 200);
            if (excerpt && !formData.excerpt) {
                setFormData(prev => ({ ...prev, excerpt: excerpt }));
            }
        }
    };

    const handleSEOChange = (e) => {
        setFormData({
            ...formData,
            seo: { ...formData.seo, [e.target.name]: e.target.value }
        });
    };

    // ✅ Image upload handler (same as video thumbnail)
    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('Image too large. Max 5MB', 'error');
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setFormData({ ...formData, imgData: reader.result, img: '' });
                setImageSourceType('local');
                showToast(`Selected: ${file.name}`);
            };
            reader.readAsDataURL(file);
        }
    };

    // ✅ Handle URL image input
    const handleUrlImageChange = (e) => {
        const url = e.target.value;
        setFormData({ ...formData, img: url, imgData: '' });
        setImagePreview(url);
        setImageSourceType('url');
    };

    const addJoke = () => {
        setJokesList([...jokesList, { title: '', content: '' }]);
    };

    const removeJoke = (index) => {
        if (jokesList.length === 1) {
            showToast('You need at least one joke', 'error');
            return;
        }
        const newList = jokesList.filter((_, i) => i !== index);
        setJokesList(newList);
    };

    const updateJoke = (index, field, value) => {
        const newList = [...jokesList];
        newList[index][field] = value;
        setJokesList(newList);
    };

    const autoGenerateSEO = () => {
        const metaTitle = formData.seo.metaTitle || formData.title;
        const metaDescription = formData.seo.metaDescription || formData.excerpt?.substring(0, 160);
        const keywords = formData.tags || formData.category;

        setFormData({
            ...formData,
            seo: {
                ...formData.seo,
                metaTitle: metaTitle,
                metaDescription: metaDescription,
                metaKeywords: keywords,
                ogTitle: metaTitle,
                ogDescription: metaDescription,
                twitterTitle: metaTitle,
                twitterDescription: metaDescription
            }
        });
        showToast('✅ SEO fields auto-generated!');
    };

    const calculateSEOScore = () => {
        let score = 0;
        if (formData.seo.metaTitle && formData.seo.metaTitle.length >= 30 && formData.seo.metaTitle.length <= 60) score += 20;
        if (formData.seo.metaDescription && formData.seo.metaDescription.length >= 120 && formData.seo.metaDescription.length <= 160) score += 20;
        if (formData.seo.metaKeywords) score += 10;
        if (formData.seo.focusKeyword && formData.content.includes(formData.seo.focusKeyword)) score += 20;
        if (formData.tags) score += 10;
        if (formData.excerpt && formData.excerpt.length > 50) score += 10;
        if (formData.img || formData.imgData) score += 10;
        return score;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title) {
            showToast('Please enter a headline', 'error');
            return;
        }

        if (!formData.category) {
            showToast('Please select a category', 'error');
            return;
        }

        let finalContent = formData.content;
        if (isJokeCollection) {
            const hasValidJokes = jokesList.some(joke => joke.content.trim());
            if (!hasValidJokes) {
                showToast('Please add at least one joke', 'error');
                return;
            }
            finalContent = generateJokeContent();
        } else if (!finalContent) {
            showToast('Please add content', 'error');
            return;
        }

        setLoading(true);
        try {
            const submitData = { ...formData, content: finalContent };

            if (id) {
                await api.put(`/articles/${id}`, submitData);
                showToast('Article updated successfully');
            } else {
                await api.post('/articles', submitData);
                showToast('Article created successfully');
            }
            await fetchArticles();
            navigate('/admin/articles');
        } catch (error) {
            console.error('Error saving article:', error);
            showToast('Error saving article', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,.08)' }}>
            <h3>{id ? '✏️ Edit Article' : '✍️ Add New Article'}</h3>

            {/* Article Type Toggle */}
            <div style={{
                background: '#f0f8ff',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
            }}>
                <div><strong>📝 Article Type:</strong></div>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="radio" checked={!isJokeCollection} onChange={() => {
                            setIsJokeCollection(false);
                            setFormData({ ...formData, content: '' });
                        }} />
                        📰 Regular Article
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="radio" checked={isJokeCollection} onChange={() => {
                            setIsJokeCollection(true);
                            setFormData({ ...formData, content: '' });
                        }} />
                        😂 Joke Collection
                    </label>
                </div>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: '10px', borderBottom: '2px solid #eee', marginBottom: '20px' }}>
                <button type="button" onClick={() => setActiveTab('basic')} style={{
                    padding: '10px 20px', background: activeTab === 'basic' ? 'var(--red)' : 'transparent',
                    color: activeTab === 'basic' ? '#fff' : '#666', border: 'none', cursor: 'pointer',
                    borderRadius: '5px 5px 0 0', fontWeight: '600'
                }}>📝 Basic Info</button>
                <button type="button" onClick={() => setActiveTab('seo')} style={{
                    padding: '10px 20px', background: activeTab === 'seo' ? 'var(--red)' : 'transparent',
                    color: activeTab === 'seo' ? '#fff' : '#666', border: 'none', cursor: 'pointer',
                    borderRadius: '5px 5px 0 0', fontWeight: '600'
                }}>🔍 SEO Settings</button>
                <button type="button" onClick={() => setActiveTab('advanced')} style={{
                    padding: '10px 20px', background: activeTab === 'advanced' ? 'var(--red)' : 'transparent',
                    color: activeTab === 'advanced' ? '#fff' : '#666', border: 'none', cursor: 'pointer',
                    borderRadius: '5px 5px 0 0', fontWeight: '600'
                }}>⚙️ Advanced</button>
            </div>

            <form onSubmit={handleSubmit}>
                {/* BASIC INFO TAB */}
                {activeTab === 'basic' && (
                    <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>📰 Headline *</label>
                                <input type="text" name="title" value={formData.title} onChange={handleChange} required
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    {formData.title.length} characters (Recommended: 50-60 for SEO)
                                </div>
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>📝 Excerpt / Summary</label>
                                <textarea name="excerpt" value={formData.excerpt} onChange={handleChange} rows="3"
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    {formData.excerpt.length} characters (Recommended: 150-160 for meta description)
                                </div>
                            </div>

                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🔗 SEO URL Slug</label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="text"
                                            name="slug"
                                            value={formData.slug || ''}
                                            onChange={handleSlugChange}
                                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                            placeholder="auto-generated-from-title"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={autoGenerateSlug}
                                        className="btn btn-outline"
                                        style={{ padding: '5px 15px', whiteSpace: 'nowrap' }}
                                    >
                                        🔄 Auto-Generate
                                    </button>
                                </div>
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    💡 SEO-friendly URL: domain.com/article/<strong>{formData.slug || 'your-url-slug'}</strong>
                                </div>
                            </div>

                            {/* Content Area - React Quill Editor */}
                            {!isJokeCollection ? (
                                <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                    <label>📄 Article Content (Rich Text Editor)</label>
                                    <div style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        marginTop: '5px'
                                    }}>
                                        <ReactQuill
                                            theme="snow"
                                            value={formData.content}
                                            onChange={handleContentChange}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            placeholder="Write your article here..."
                                            style={{ height: '400px', marginBottom: '50px' }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                    <label>😂 Jokes Collection</label>
                                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', background: '#fafafa' }}>
                                        <p style={{ marginBottom: '15px', fontSize: '13px', color: '#666' }}>
                                            Add multiple jokes to this collection. Each joke will appear as a separate card.
                                        </p>
                                        {jokesList.map((joke, index) => (
                                            <div key={index} style={{
                                                border: '1px solid #e0e0e0',
                                                padding: '15px',
                                                marginBottom: '15px',
                                                borderRadius: '8px',
                                                background: '#fff'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                    <strong style={{ color: 'var(--navy)' }}>Joke #{index + 1}</strong>
                                                    <button type="button" onClick={() => removeJoke(index)}
                                                        style={{ background: '#ff6b6b', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '5px', cursor: 'pointer' }}>
                                                        Remove
                                                    </button>
                                                </div>
                                                <div className="fg" style={{ marginBottom: '10px' }}>
                                                    <label>Joke Title (Optional)</label>
                                                    <input
                                                        type="text"
                                                        value={joke.title}
                                                        onChange={(e) => updateJoke(index, 'title', e.target.value)}
                                                        placeholder={`Joke ${index + 1} title`}
                                                        style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                                    />
                                                </div>
                                                <div className="fg">
                                                    <label>Joke Content *</label>
                                                    <textarea
                                                        value={joke.content}
                                                        onChange={(e) => updateJoke(index, 'content', e.target.value)}
                                                        rows="4"
                                                        placeholder="Enter the joke here..."
                                                        required
                                                        style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <button type="button" className="btn btn-outline" onClick={addJoke}
                                            style={{ marginTop: '10px', width: '100%' }}>
                                            + Add Another Joke
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="fg">
                                <label>📂 Category *</label>
                                <select name="category" value={formData.category} onChange={handleChange} required
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="">Select Category</option>
                                    <option>India</option><option>World</option><option>Politics</option><option>Business</option>
                                    <option>Sports</option><option>Entertainment</option><option>Technology</option><option>Lifestyle</option>
                                    <option>Jokes</option>
                                </select>
                            </div>

                            <div className="fg">
                                <label>📍 Section Placement</label>
                                <select name="section" value={formData.section} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option>Hero / Breaking</option><option>Top Stories</option><option>Politics</option>
                                    <option>Sports</option><option>Entertainment</option><option>Business</option><option>Technology</option>
                                </select>
                            </div>

                            <div className="fg">
                                <label>✍️ Author</label>
                                <input type="text" name="author" value={formData.author} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>

                            <div className="fg">
                                <label>🏷️ Tags (comma separated)</label>
                                <input type="text" name="tags" value={formData.tags} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                    placeholder="e.g., Airport, Delhi, Travel" />
                            </div>

                            {/* ✅ IMAGE UPLOAD SECTION - Same as Video thumbnail */}
                            <div className="fg full">
                                <label>🖼️ Cover Image</label>
                                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input type="radio" checked={imageSourceType === 'url'} onChange={() => setImageSourceType('url')} />
                                        🔗 Image URL
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input type="radio" checked={imageSourceType === 'local'} onChange={() => setImageSourceType('local')} />
                                        📁 Upload Image
                                    </label>
                                </div>

                                {imageSourceType === 'url' ? (
                                    <input
                                        type="url"
                                        name="img"
                                        value={formData.img}
                                        onChange={handleUrlImageChange}
                                        style={{ width: '100%', padding: '10px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                        placeholder="https://example.com/image.jpg"
                                    />
                                ) : (
                                    <div>
                                        <input
                                            type="file"
                                            onChange={handleImageFileChange}
                                            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                                            style={{ width: '100%', padding: '8px', border: '1.5px solid #ddd', borderRadius: '5px' }}
                                        />
                                        {imageFile && (
                                            <p style={{ fontSize: '12px', color: 'green', marginTop: '5px' }}>
                                                ✅ Selected: {imageFile.name} ({(imageFile.size / 1024).toFixed(2)} KB)
                                            </p>
                                        )}
                                    </div>
                                )}

                                {imagePreview && (
                                    <div style={{ marginTop: '15px' }}>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{ maxWidth: '200px', maxHeight: '120px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ddd' }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* SEO TAB */}
                {activeTab === 'seo' && (
                    <div>
                        <div style={{ background: '#f0f8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                <div>
                                    <strong>📊 SEO Score: {calculateSEOScore()}%</strong>
                                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                                        {calculateSEOScore() >= 80 ? '✅ Excellent!' :
                                            calculateSEOScore() >= 60 ? '⚠️ Good!' :
                                                '🔴 Needs improvement'}
                                    </div>
                                </div>
                                <button type="button" onClick={autoGenerateSEO} className="btn btn-navy" style={{ padding: '5px 15px' }}>
                                    🤖 Auto-Generate SEO
                                </button>
                            </div>
                            <div style={{ height: '6px', background: '#ddd', borderRadius: '3px', marginTop: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${calculateSEOScore()}%`, height: '100%', background: calculateSEOScore() >= 80 ? 'green' : calculateSEOScore() >= 60 ? 'orange' : 'red' }}></div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🎯 Focus Keyword</label>
                                <input type="text" name="focusKeyword" value={formData.seo.focusKeyword} onChange={handleSEOChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>
                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🔖 Meta Title</label>
                                <input type="text" name="metaTitle" value={formData.seo.metaTitle} onChange={handleSEOChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>
                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>📝 Meta Description</label>
                                <textarea name="metaDescription" value={formData.seo.metaDescription} onChange={handleSEOChange} rows="3"
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}></textarea>
                            </div>
                            <div className="fg full" style={{ gridColumn: '1/-1' }}>
                                <label>🔑 Meta Keywords</label>
                                <input type="text" name="metaKeywords" value={formData.seo.metaKeywords} onChange={handleSEOChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ADVANCED TAB */}
                {activeTab === 'advanced' && (
                    <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="fg">
                                <label>📌 Status</label>
                                <select name="status" value={formData.status} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="live">✅ Live</option>
                                    <option value="draft">📝 Draft</option>
                                </select>
                            </div>
                            <div className="fg">
                                <label>⚡ Breaking News</label>
                                <select name="breaking" value={formData.breaking} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="no">No</option>
                                    <option value="yes">🔴 Yes</option>
                                </select>
                            </div>
                            <div className="fg">
                                <label>⭐ Featured</label>
                                <select name="featured" value={formData.featured} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }}>
                                    <option value="no">No</option>
                                    <option value="yes">⭐ Yes</option>
                                </select>
                            </div>
                            <div className="fg">
                                <label>📅 Published Date</label>
                                <input type="date" name="date" value={formData.date} onChange={handleChange}
                                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '5px' }} />
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                    <button type="submit" className="btn btn-red" disabled={loading}>
                        {loading ? '⏳ Saving...' : (id ? '💾 Update Article' : '💾 Create Article')}
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/articles')}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ArticleForm;